import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { PlaygroundCaseItem } from '@repo/shared-types';
import { Input, Textarea } from '../ui/input';
import { api, type PlaygroundCase, type PlaygroundFeedItem } from '../../lib/api';
import { formatSource, useSourceLabels } from '../../lib/source-label';
import { cn } from '../../lib/utils';

export type ContentSource =
  | { kind: 'inline'; label: string; items: PlaygroundCaseItem[] }
  | { kind: 'cases'; caseIds: string[] };

/**
 * What to run against: real arrivals, saved cases, or something pasted.
 *
 * The feed tab is first because it is the one that matters. Placement judged on invented content is
 * placement judged against a knowledge base nobody has — the whole difficulty is that a real partner
 * call names two engagements, uses a vendor's name three projects share, and buries the decision
 * under scheduling chatter. Synthetic content is uniformly easier than that, in a way that flatters
 * every configuration equally.
 */
export function ContentPicker({
  cases,
  value,
  onChange,
  disabled,
  mode = 'arrival',
}: {
  cases: PlaygroundCase[];
  value: ContentSource;
  onChange: (next: ContentSource) => void;
  disabled?: boolean;
  /** `question` is the retrieval agent: one question typed, or saved question cases. */
  mode?: 'arrival' | 'question';
}) {
  const [tab, setTab] = useState<'feed' | 'cases' | 'paste' | 'question'>(
    value.kind === 'cases' ? 'cases' : mode === 'question' ? 'question' : 'feed',
  );
  const tabs =
    mode === 'question'
      ? ([
          ['question', 'Ask'],
          ['cases', `Cases${cases.length ? ` (${cases.length})` : ''}`],
        ] as const)
      : ([
          ['feed', 'Real arrivals'],
          ['cases', `Cases${cases.length ? ` (${cases.length})` : ''}`],
          ['paste', 'Paste'],
        ] as const);

  return (
    <div>
      <div className="mb-2.5 flex gap-1 rounded-lg bg-surface-muted p-0.5">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={cn(
              'flex-1 rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors',
              tab === key ? 'bg-card text-ink shadow-sm' : 'text-ink-muted hover:text-ink',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'feed' && <FeedTab onPick={onChange} disabled={disabled} selected={value} />}
      {tab === 'cases' && <CasesTab cases={cases} value={value} onChange={onChange} disabled={disabled} />}
      {tab === 'paste' && <PasteTab value={value} onChange={onChange} disabled={disabled} />}
      {tab === 'question' && <QuestionTab value={value} onChange={onChange} disabled={disabled} />}
    </div>
  );
}

/** The retrieval agent's input: the question, exactly as someone would ask it. */
function QuestionTab({
  value,
  onChange,
  disabled,
}: {
  value: ContentSource;
  onChange: (next: ContentSource) => void;
  disabled?: boolean;
}) {
  const question = value.kind === 'inline' ? (value.items[0]?.content ?? '') : '';
  return (
    <div className="space-y-1.5">
      <Textarea
        value={question}
        onChange={(e) => {
          const content = e.target.value;
          const title = content.replace(/\s+/g, ' ').trim().slice(0, 120) || null;
          onChange({
            kind: 'inline',
            label: title ?? '',
            items: content ? [{ content, source: 'playground', title, sourceTimestamp: null, feedItemId: null }] : [],
          });
        }}
        rows={4}
        placeholder="What did we decide about pricing for the pilot?"
        disabled={disabled}
        className="text-xs"
      />
      <p className="text-[10px] leading-relaxed text-ink-faint">
        Asked from the organization root, so every project is in view — as an ask from the console
        or an MCP client with organization membership would be.
      </p>
    </div>
  );
}

function FeedTab({
  onPick,
  disabled,
  selected,
}: {
  onPick: (next: ContentSource) => void;
  disabled?: boolean;
  selected: ContentSource;
}) {
  const [items, setItems] = useState<PlaygroundFeedItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const sourceLabels = useSourceLabels();

  useEffect(() => {
    api.playground
      .feed({ limit: 40 })
      .then(setItems)
      .catch((e: Error) => setError(e.message));
  }, []);

  const pick = async (item: PlaygroundFeedItem) => {
    setLoadingId(item.id);
    try {
      const full = await api.playground.feedItem(item.id);
      onPick({
        kind: 'inline',
        label: full.title ?? full.source,
        items: [
          {
            content: full.content,
            source: full.source,
            title: full.title,
            // Carried through deliberately: the prompt renders each item's own date, and the rule
            // that older material is history rather than an update turns on it. An arrival replayed
            // without its date is not the arrival.
            sourceTimestamp: full.capturedAt,
            feedItemId: full.id,
          },
        ],
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoadingId(null);
    }
  };

  if (error) return <p className="px-1 py-3 text-[11px] text-danger-text">{error}</p>;
  if (!items) return <Loading />;
  if (items.length === 0)
    return <p className="px-1 py-3 text-[11px] text-ink-faint">Nothing has arrived yet.</p>;

  const activeId = selected.kind === 'inline' ? selected.items[0]?.feedItemId : null;

  return (
    <div className="max-h-64 space-y-1 overflow-y-auto pr-0.5">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          disabled={disabled || loadingId !== null}
          onClick={() => pick(item)}
          className={cn(
            'w-full rounded-lg border px-2.5 py-2 text-left transition-colors disabled:opacity-50',
            activeId === item.id ? 'border-orange-400 bg-orange-50 dark:bg-orange-950/30' : 'border-border hover:bg-surface-subtle',
          )}
        >
          <div className="flex items-center gap-1.5">
            <span className="truncate text-[11px] font-medium text-ink">
              {item.title ?? formatSource(item.source, sourceLabels)}
            </span>
            {loadingId === item.id && <Loader2 size={11} className="animate-spin text-ink-faint" />}
            <span className="ml-auto flex-shrink-0 text-[10px] tabular-nums text-ink-faint">
              {item.chars.toLocaleString()} chars
            </span>
          </div>
          <p className="mt-0.5 truncate text-[10px] text-ink-faint">
            {item.sourceType} · {new Date(item.capturedAt).toLocaleDateString()} · {item.excerpt}
          </p>
        </button>
      ))}
    </div>
  );
}

function CasesTab({
  cases,
  value,
  onChange,
  disabled,
}: {
  cases: PlaygroundCase[];
  value: ContentSource;
  onChange: (next: ContentSource) => void;
  disabled?: boolean;
}) {
  const selected = value.kind === 'cases' ? value.caseIds : [];

  const toggle = (id: string) => {
    const next = selected.includes(id) ? selected.filter((one) => one !== id) : [...selected, id];
    onChange({ kind: 'cases', caseIds: next });
  };

  if (cases.length === 0) {
    return (
      <p className="px-1 py-3 text-[11px] leading-relaxed text-ink-faint">
        No cases yet. Save a run’s content as a case to build a suite you can run again after every
        change — one run tells you what happened, a suite tells you whether it got better.
      </p>
    );
  }

  return (
    <div>
      <div className="max-h-56 space-y-1 overflow-y-auto pr-0.5">
        {cases.map((one) => (
          <button
            key={one.id}
            type="button"
            disabled={disabled}
            onClick={() => toggle(one.id)}
            className={cn(
              'w-full rounded-lg border px-2.5 py-2 text-left transition-colors disabled:opacity-50',
              selected.includes(one.id)
                ? 'border-orange-400 bg-orange-50 dark:bg-orange-950/30'
                : 'border-border hover:bg-surface-subtle',
            )}
          >
            <div className="flex items-center gap-1.5">
              <span className="truncate text-[11px] font-medium text-ink">{one.name}</span>
              {one.expectation && (
                <span className="rounded bg-info-bg px-1.5 py-px text-[9px] font-medium text-info-text">graded</span>
              )}
              <span className="ml-auto flex-shrink-0 text-[10px] tabular-nums text-ink-faint">
                {one.items.length > 1 ? `${one.items.length} items · ` : ''}
                {one.chars.toLocaleString()} chars
              </span>
            </div>
            {one.notes && <p className="mt-0.5 truncate text-[10px] text-ink-faint">{one.notes}</p>}
          </button>
        ))}
      </div>
      {selected.length > 1 && (
        <p className="mt-1.5 text-[10px] text-ink-faint">
          {selected.length} cases — one run each, grouped as a suite.
        </p>
      )}
    </div>
  );
}

function PasteTab({
  value,
  onChange,
  disabled,
}: {
  value: ContentSource;
  onChange: (next: ContentSource) => void;
  disabled?: boolean;
}) {
  const item = value.kind === 'inline' ? value.items[0] : undefined;
  const content = item?.content ?? '';

  const update = (patch: Partial<PlaygroundCaseItem>) => {
    const base: PlaygroundCaseItem = item ?? {
      content: '',
      source: 'playground',
      title: null,
      sourceTimestamp: null,
      feedItemId: null,
    };
    const next = { ...base, ...patch };
    onChange({ kind: 'inline', label: next.title ?? next.source, items: [next] });
  };

  return (
    <div className="space-y-2">
      <Input
        value={item?.title ?? ''}
        onChange={(e) => update({ title: e.target.value || null })}
        placeholder="Title (optional)"
        disabled={disabled}
        className="text-xs"
      />
      <Textarea
        value={content}
        onChange={(e) => update({ content: e.target.value })}
        placeholder="Paste a meeting note, a thread, a document…"
        disabled={disabled}
        rows={10}
        className="text-[11px] leading-relaxed"
      />
      <p className="text-[10px] leading-relaxed text-ink-faint">
        {content.length.toLocaleString()} characters. Pasted content carries no date, so the agent is
        told the material’s age is unknown — which is one of the signals it uses. Lifting a real
        arrival instead keeps it.
      </p>
    </div>
  );
}

function Loading() {
  return (
    <div className="flex items-center gap-2 px-1 py-3 text-[11px] text-ink-faint">
      <Loader2 size={12} className="animate-spin" />
      Loading
    </div>
  );
}

/** Whether this selection can be run at all, and why not when it cannot. */
export function contentProblem(value: ContentSource): string | null {
  if (value.kind === 'cases') return value.caseIds.length > 0 ? null : 'Pick at least one case.';
  const chars = value.items.reduce((sum, item) => sum + item.content.length, 0);
  if (chars === 0) return 'Give it something to work on.';
  return null;
}
